import { ProductPricing } from '@galaxy/billing';
import {
  AppPrice,
  AppPrices,
  Commitment,
  Discount,
  Fee,
  FeeAmountType,
  FeeType,
  PriceDisplayOverride,
  PricingRule,
} from '@galaxy/marketplace-apps';
import { calculateFeeAmount, toGalaxyFrequency } from '@vendasta/billing-ui';
import { Frequency as GalaxyFrequency } from '@vendasta/galaxy/frequency';

export type PriceOverride = 'CONTACT SALES' | 'FREE';

export interface OrderConfirmationItem {
  name: string;
  icon: string;
  quantity: number;
  activationMessage: string;
  requiresApproval: boolean;
  priceOverride: PriceOverride;
  appId: string;
  addonId: string;
  editionId?: string;
  editionName?: string;
  tagline?: string;
  initialSubtotalCost?: number;
  retailTotal?: number;
  appPrices: AppPrices;
  effectiveRetailAppPrice?: AppPrice;
  effectiveAppPrice?: AppPrice;
  generalPriceInfo?: GeneralPriceInfo;
  generalPriceInfoRetail?: GeneralPriceInfo;
  generalPriceInfoInstanceRetail?: GeneralPriceInfo;
  partnerCurrency: string;
  productPricing: ProductPricing;
  marketCurrency: string;
}

export type ActivationErrorType = 'activationFailure' | 'unknownFailure' | 'noAppsError' | 'invalidForm';
export interface ActivationError {
  appNames: string[];
  type: ActivationErrorType;
  errorMessage: string;
  openFailureDialog: boolean;
}

export interface GeneralPriceInfo {
  price: number;
  frequency: GalaxyFrequency;
  setupFee: number;
  commitment: Commitment;
  stairSteps: PricingRule[];
  discounts: Discount[];
  fees: Fee[];
  contactSales: boolean;
}

export function extractGeneralPriceInfoFromOrderConfirmation(
  item: OrderConfirmationItem,
  context = 'wholesale',
): GeneralPriceInfo {
  let appPrice = item.effectiveAppPrice ? item.effectiveAppPrice : item.appPrices.pricesForContexts[context];
  let currency = item.partnerCurrency;

  if (context === 'retail') {
    appPrice = item.effectiveRetailAppPrice;
    currency = item.marketCurrency;
  }

  if (appPrice && appPrice.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
    return {
      contactSales: true,
    } as GeneralPriceInfo;
  }

  if (
    !appPrice ||
    !appPrice.pricesForEditions?.[item.editionId || '']?.pricesForCurrencies?.[currency]?.pricesForFrequencies?.[0]
      ?.pricingRules?.[0].price
  ) {
    return {} as GeneralPriceInfo;
  }

  const freqPrice =
    appPrice.pricesForEditions[item.editionId || ''].pricesForCurrencies[currency].pricesForFrequencies[0];
  let price = freqPrice.pricingRules[0].price;

  if (context === 'wholesale') {
    const setupFee = freqPrice.setupFee || 0;
    price = freqPrice.instancePrice.total - setupFee;
  }

  const priceInfo = {
    price: price,
    frequency: toGalaxyFrequency(freqPrice.frequency),
    setupFee: freqPrice.setupFee * item.quantity,
    stairSteps: freqPrice.pricingRules.length > 1 ? freqPrice.pricingRules : null,
    discounts: appPrice.pricesForEditions[item.editionId || ''].discount || [],
  } as GeneralPriceInfo;

  if (freqPrice?.commitment?.initial > 1 || freqPrice?.commitment?.recurring > 1) {
    priceInfo.commitment = freqPrice.commitment;
  }

  if (freqPrice?.fees?.length > 0) {
    const feeAmount = calculateFeeAmount(priceInfo.price, freqPrice.fees);
    const calculatedFee = {
      amount: feeAmount * item.quantity,
      amountType: FeeAmountType.FEE_AMOUNT_TYPE_FIXED,
      feeType: FeeType.FEE_TYPE_MANAGEMENT,
    } as Fee;
    priceInfo.fees = [calculatedFee];
  }

  return priceInfo;
}
